/* Anyone can check whether a text was written by PathsAbroad's AI: the free check that goes with the machine-readable
   marking of AI answers (EU AI Act Art. 50(2)). The API compares a fingerprint of the text with the fingerprints of AI
   answers; nothing about the student is shown. */
import { useState, type FormEvent } from 'react';
import type { AiVerifyResponse } from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { Alert, Button, Field, PageHeader } from '../app/ui';
import { api, errorAction } from '../lib/api';
import { formatDate } from '../lib/format';
import { translate } from '../lib/i18n';

export function AiCheck() {
  const { t, lang } = useT();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AiVerifyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!text.trim()) { setError(t('aiCheck.empty')); document.getElementById('ai-text')?.focus(); return; }
    setBusy(true); setError(null); setResult(null);
    try {
      setResult(await api.post<AiVerifyResponse>('/api/ai/verify', { text }));
    } catch (er) {
      const a = errorAction(er, '', ['signed_out', 'mfa_required', 'guardian_consent_required', 'plan_required']);
      setError(a.kind === 'message' ? translate(lang, a.key) : translate(lang, 'err.server_error'));
    } finally { setBusy(false); }
  };

  return (
    <div className="narrow">
      <PageHeader title={t('aiCheck.title')} lead={t('aiCheck.lead')} />
      <form className="card stack" noValidate onSubmit={submit} aria-labelledby="page-title">
        <Field id="ai-text" label={t('aiCheck.label')} hint={t('aiCheck.hint')} error={error}>
          {a => <textarea {...a} className="input textarea" rows={8} maxLength={60000} dir="auto" value={text} onChange={e => { setText(e.target.value); setResult(null); }} />}
        </Field>
        <Button type="submit" block busy={busy}>{t('aiCheck.submit')}</Button>
      </form>
      <div className="stack" aria-live="polite">
        {result && (result.ai_generated
          ? <Alert tone="info" title={t('aiCheck.yes.title')}>{t('aiCheck.yes.text', { date: formatDate(result.created_at ?? null, lang) })}</Alert>
          : <Alert tone="success" title={t('aiCheck.no.title')}>{t('aiCheck.no.text')}</Alert>)}
        <p className="muted">{t('aiCheck.how')}</p>
      </div>
    </div>
  );
}
